import React, { useEffect, useState } from 'react';
import { Button, Spin, Tooltip, message } from 'antd';
import { CommentOutlined, UpOutlined } from '@ant-design/icons';
import './engagement.less';
import { EngagementConfig, EngagementSummaryItem, ReactionsSummary } from './constants';
import { engagementThreadKey, setReactionApi, toggleReaction, useEngagementThread, useSetReaction } from './engagementApi';
import { EngagementLayerProvider, useEngagementTranslations } from './EngagementTranslationsContext';
import CommentsThread from './CommentsThread';
import LinkifiedText from './LinkifiedText';
import ReactionBar from './ReactionBar';

interface Props {
  modelType: number;
  modelId: string | number;
  /**
   * Tight places (video modal, guide panel, lists): reactions + "💬 n"; the latest comment as one line with
   * "show more"; the full thread and the composer only after a click.
   */
  compact?: boolean;
  /**
   * List item data from useEngagementSummary - the panel then shows it without loading the thread, and loads the
   * thread only when the comments are opened (one summary request for the whole list instead of one per item).
   */
  summary?: { config: EngagementConfig; item?: EngagementSummaryItem };
  className?: string;
  /** popups above this layer - e.g. inside the video notification modal */
  zIndex?: number;
}

/** reactions and comments of one entity */
const EngagementPanel: React.FC<Props> = ({ modelType, modelId, compact, summary, className, zIndex }) => {
  const t = useEngagementTranslations();
  const id = String(modelId);
  const threadKey = engagementThreadKey(modelType, id);
  const [isExpanded, setIsExpanded] = useState(!compact);
  // list mode: reactions kept locally until the thread is loaded
  const [listReactions, setListReactions] = useState<ReactionsSummary | null>(summary?.item?.reactions ?? null);
  useEffect(() => {
    setListReactions(summary?.item?.reactions ?? null);
  }, [summary?.item?.reactions]);

  const listMode = !!summary && !isExpanded;
  const thread = useEngagementThread(modelType, id, !listMode);
  const setReaction = useSetReaction(threadKey);

  const firstPage = thread.data?.pages?.[0];
  const config = firstPage?.config ?? summary?.config;
  const reactions = listMode ? listReactions : firstPage?.reactions;
  const commentCount = firstPage?.comment_count ?? summary?.item?.comment_count ?? 0;

  if (!t || !config || (!config.comments && !config.reactions)) {
    return thread.isLoading && !listMode ? <Spin size="small" /> : null;
  }

  const react = (emoji: string) => {
    if (listMode) {
      const previous = listReactions || { counts: {}, mine: null };
      setListReactions(toggleReaction(previous, emoji));
      setReactionApi(modelType, id, emoji === previous.mine ? null : emoji)
        .then((res) => res?.reactions && setListReactions(res.reactions))
        .catch(() => {
          setListReactions(previous);
          message.error(t.error);
        });
      return;
    }
    const mine = firstPage?.reactions?.mine;
    setReaction.mutate(
      { modelType, modelId: id, emoji: emoji === mine ? null : emoji },
      {
        onError: () => {
          message.error(t.error);
        },
      }
    );
  };

  const latest = firstPage?.comments?.[0];
  const pages = thread.data?.pages || [];

  return (
    <EngagementLayerProvider value={zIndex}>
    <div className={`engagement-panel ${compact ? 'engagement-panel--compact' : ''} ${className || ''}`}>
      <div className="engagement-panel__bar">
        {config.reactions && <ReactionBar summary={reactions} emojis={config.emojis} onReact={react} />}
        {config.comments && compact && (
          <Tooltip title={isExpanded ? t.hideComments : t.showComments} zIndex={zIndex}>
            <Button
              type="text"
              size="small"
              className="engagement-panel__comments-toggle"
              icon={isExpanded ? <UpOutlined /> : <CommentOutlined />}
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              {isExpanded ? t.showLess : commentCount > 0 ? commentCount : t.addComment}
            </Button>
          </Tooltip>
        )}
      </div>

      {config.comments && compact && !isExpanded && latest && (
        // one line of the latest comment - the rest behind "show more"
        <button
          type="button"
          className="engagement-panel__preview"
          title={t.showComments}
          onClick={() => setIsExpanded(true)}
        >
          <span className="engagement-panel__preview-author">{latest.author.name}:</span>
          <LinkifiedText text={latest.body} className="engagement-panel__preview-body" />
          <span className="engagement-panel__preview-more">{t.showMore}</span>
        </button>
      )}

      {config.comments && isExpanded && (
        <Spin spinning={thread.isLoading} size="small">
          {pages.length > 0 && (
            <CommentsThread
              modelType={modelType}
              modelId={id}
              threadKey={threadKey}
              pages={pages}
              hasNextPage={thread.hasNextPage}
              isFetchingNextPage={thread.isFetchingNextPage}
              fetchNextPage={() => thread.fetchNextPage()}
            />
          )}
        </Spin>
      )}
    </div>
    </EngagementLayerProvider>
  );
};

export default EngagementPanel;
